import { IBlock } from "../../../framework/src/IBlock";
import { Message } from "../../../framework/src/Message";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import { runEngine } from "../../../framework/src/RunEngine";

// Customizable Area Start
import { makeApiMessage } from "../../../components/src/common";
import { ISortingData } from "../../../components/src/SortingTableHeader2";

export const configJSON = require("./config");

export interface IScheduleItem {
  id: number;
  name: string;
  report_type: string;
  report_type_label: string;
  rrule: string;
  execution_time: string;
  timezone: string;
  start_date: string;
  end_date: string | null;
  last_run_at: string | null;
  next_run_at: string;
  custom_params: {
    store_ids: string;
    region_ids: string;
    relative_days: number;
    filter_columns: string[];
    date_range_type: string;
    business_account_id: string;
  };
  email_subject: string;
  email_body: string;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  recipients: {
    to: { id: number; email: string }[];
    cc: { id: number; email: string }[];
  };
}

export interface PermissionStatus {
  mainPermission: boolean;
  createPermission: boolean;
  viewPermission: boolean;
  editPermission: boolean;
  deactivatePermission: boolean;
  activatePermission: boolean;
}
// Customizable Area End

export interface Props {
  navigation: {
    navigate: Function;
    getParam: Function;
  };
  id: string;
  // Customizable Area Start
  // Customizable Area End
}

export interface S {
  // Customizable Area Start
  schedulesList: IScheduleItem[];
  totalCount: number;
  isLoading: boolean;
  sortingData: ISortingData;
  permissionStatus: PermissionStatus;
  isLoadingPermission: boolean;
  popOverOpened: boolean;
  popOverTop: number;
  popOverLeft: number;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error";
  // Customizable Area End
}

interface SS {
  id: any;
  // Customizable Area Start
  // Customizable Area End
}

export default class ReportSchedulerController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  schedulesListMessageId: string = "";
  runScheduleApiCallId: string = "";
  currentPage: number = 1;
  rowPerPage: number = 10;
  sortQuery: string = "";
  popOverItemId: number | string = "";

  sortingProps = {
    width: "20%",
    onQueryChange: (query: string) => {
      this.sortQuery = query;
      this.getSchedulesApi();
    },
    onChange: (sortingData: ISortingData) => this.setState({ sortingData }),
  };
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.receive = this.receive.bind(this);

    // Customizable Area Start
    this.subScribedMessages = [
      getName(MessageEnum.AccoutLoginSuccess),
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.RestAPIResponceSuccessMessage),
      getName(MessageEnum.RestAPIResponceDataMessage),
    ];

    this.state = {
      schedulesList: [],
      totalCount: 0,
      isLoading: false,
      sortingData: {
        name: "",
        report_type_label: "",
        start_date: "",
        end_date: "",
      },
      permissionStatus: {
        mainPermission: true,
        createPermission: true,
        viewPermission: true,
        editPermission: true,
        deactivatePermission: true,
        activatePermission: true,
      },
      isLoadingPermission: false,
      popOverOpened: false,
      popOverTop: 0,
      popOverLeft: 0,
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
    };
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    // Customizable Area End
  }

  async receive(from: string, message: Message) {
    runEngine.debugLog("Message Received", message);

    // Customizable Area Start
    const requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );

    const successResponse = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (successResponse) {
      if (requestId === this.schedulesListMessageId) {
        this.handleSchedulesListResponse(successResponse);
      }
      if (requestId === this.runScheduleApiCallId) {
        this.handleRunScheduleResponse(successResponse);
      }
    }
    // Customizable Area End
  }

  // Customizable Area Start
  async componentDidMount() {
    this.getSchedulesApi();
  }

  getSchedulesApi = () => {
    this.setState({ isLoading: true });

    let url = `bx_block_reportscheduler/report_schedules?page=${this.currentPage}&per_page=${this.rowPerPage}`;

    if (this.sortQuery) {
      url += `&${this.sortQuery}`;
    }

    const requestMessage = makeApiMessage({
      url: url,
      method: "GET",
    });

    this.schedulesListMessageId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleSchedulesListResponse = (responseJson: {
    data?: IScheduleItem[];
    meta?: { total: number };
  }) => {
    this.setState({ isLoading: false });

    if (responseJson && responseJson.data) {
      this.setState({
        schedulesList: responseJson.data,
        totalCount: responseJson.meta?.total || responseJson.data.length,
      });
    }
  };

  onChangePage = (page: number) => {
    this.currentPage = page;
    this.getSchedulesApi();
  };

  handleAddSchedule = () => {
    this.props.navigation.navigate("ReportSchedulerCreate");
  };

  handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>, itemId: number) => {
    const position = event.currentTarget.getBoundingClientRect();
    this.popOverItemId = itemId;
    this.setState({
      popOverOpened: true,
      popOverTop: position.top + window.scrollY,
      popOverLeft: position.left + window.scrollX,
    });
  };

  handleClosePopover = () => {
    this.setState({ popOverOpened: false });
  };

  handleViewSchedule = () => {
    this.setState({ popOverOpened: false });
    this.props.navigation.navigate("ReportSchedulerView", { id: this.popOverItemId });
  };

  handleEditSchedule = () => {
    this.setState({ popOverOpened: false });
    this.props.navigation.navigate("ReportSchedulerEdit", { id: this.popOverItemId });
  };

  handleRunSchedule = () => {
    this.setState({ popOverOpened: false });

    const requestMessage = makeApiMessage({
      url: `bx_block_reportscheduler/report_schedules/${this.popOverItemId}/run_now`,
      method: "POST",
    });

    this.runScheduleApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleRunScheduleResponse = (responseJson: any) => {
    if (responseJson && (responseJson.data || responseJson.message)) {
      this.setState({
        snackbarOpen: true,
        snackbarMessage: responseJson.message || "Schedule triggered successfully!",
        snackbarSeverity: "success",
      });
    } else if (responseJson && responseJson.errors) {
      const errorMessage = Array.isArray(responseJson.errors)
        ? responseJson.errors.join(", ")
        : responseJson.errors;
      this.setState({
        snackbarOpen: true,
        snackbarMessage: errorMessage || "Failed to run schedule",
        snackbarSeverity: "error",
      });
    }
  };

  handleCloseSnackbar = () => {
    this.setState({ snackbarOpen: false });
  };
  // Customizable Area End
}
